import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CalendarRange, CheckCircle2, Loader2, PackageOpen, Wand2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";

/**
 * Feb 2026 — Reusable dialog that shows the diff between what a Package
 * expects to produce in a given month and what already lives on the
 * Content Calendar. One click on "Create missing" writes only the
 * missing items via the existing idempotent `generate` endpoint.
 *
 * Used from:
 *   • Package Detail → "Sync to Content Calendar" button
 *   • Content Calendar → "Compare with Package" button
 */
export default function PackageCalendarSyncDialog({
  open,
  onClose,
  workspaceId,
  packageId,
  packageName,
  defaultMonth,
  onSynced,
}) {
  const [month, setMonth] = useState(defaultMonth || currentMonth());
  const [inheritAssignee, setInheritAssignee] = useState(true);
  const [diff, setDiff] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDiff(null);
    setMonth(defaultMonth || currentMonth());
  }, [open, defaultMonth, packageId]);

  const runCompare = async () => {
    if (!workspaceId || !packageId) return;
    setLoading(true);
    try {
      const { data } = await api.get(
        `/workspaces/${workspaceId}/packages/${packageId}/calendar-diff`,
        { params: { month }, timeout: 30000 });
      setDiff(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Compare failed")); }
    finally { setLoading(false); }
  };

  const createMissing = async () => {
    if (!diff || !diff.totals?.missing) return;
    setCreating(true);
    try {
      const { data } = await api.post(
        `/workspaces/${workspaceId}/content-calendar/generate`,
        { month, package_id: packageId, inherit_assignee: inheritAssignee });
      toast.success(`Created ${data.created} content item${data.created === 1 ? "" : "s"}${data.tasks_created ? ` · ${data.tasks_created} tasks` : ""}`);
      onSynced && onSynced(data);
      // Refresh diff so the "missing" column goes to 0.
      await runCompare();
    } catch (e) { toast.error(apiErrorMessage(e, "Sync failed")); }
    finally { setCreating(false); }
  };

  const totals = diff?.totals || { expected: 0, existing: 0, missing: 0 };
  const monthLabel = useMemo(() => {
    try {
      const [y, m] = month.split("-").map(Number);
      return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
    } catch { return month; }
  }, [month]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto rounded-none" data-testid="pkg-calendar-sync-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <PackageOpen className="w-4 h-4" />
            <span>Sync to Content Calendar</span>
            {packageName && <span className="text-xs font-normal text-muted-foreground truncate">· {packageName}</span>}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-end gap-2 flex-wrap">
            <div>
              <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Month</label>
              <input
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="block h-9 px-2 border border-border bg-background text-sm mt-1"
                data-testid="pkg-sync-month"
              />
            </div>
            <Button
              onClick={runCompare}
              disabled={loading || !month}
              variant="outline"
              size="sm"
              className="rounded-none h-9"
              data-testid="pkg-sync-compare-btn"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CalendarRange className="w-3.5 h-3.5 mr-1" />}
              {diff ? "Refresh" : "Compare"}
            </Button>
            <label className="inline-flex items-center gap-1.5 text-xs ml-auto cursor-pointer" title="Inherit assignee from each package skill row when spawning tasks">
              <input
                type="checkbox"
                checked={inheritAssignee}
                onChange={(e) => setInheritAssignee(e.target.checked)}
                data-testid="pkg-sync-inherit-assignee"
              />
              <span>Inherit skill assignees</span>
            </label>
          </div>

          {!diff && !loading && (
            <div className="text-sm text-muted-foreground py-10 text-center border border-dashed border-border" data-testid="pkg-sync-empty">
              Pick a month and click <strong>Compare</strong> to see the diff.
            </div>
          )}

          {diff && (
            <>
              <div className="grid grid-cols-3 gap-2">
                <SummaryTile label="Expected" value={totals.expected} testid="pkg-sync-total-expected" />
                <SummaryTile label="Already on calendar" value={totals.existing} tone="emerald" testid="pkg-sync-total-existing" />
                <SummaryTile
                  label="Missing"
                  value={totals.missing}
                  tone={totals.missing > 0 ? "rose" : "muted"}
                  testid="pkg-sync-total-missing"
                />
              </div>

              <div className="border border-border">
                <div className="grid grid-cols-[minmax(0,1fr)_90px_90px_90px_100px] text-[10px] uppercase tracking-wide text-muted-foreground bg-muted/40 px-3 py-2">
                  <div>Line</div>
                  <div className="text-center">Expected</div>
                  <div className="text-center">Existing</div>
                  <div className="text-center">Missing</div>
                  <div className="text-center">Frequency</div>
                </div>
                <div className="max-h-72 overflow-y-auto divide-y divide-border">
                  {(diff.lines || []).length === 0 && (
                    <div className="text-xs text-muted-foreground text-center py-6">
                      This package has no lines.
                    </div>
                  )}
                  {(diff.lines || []).map((ln) => (
                    <div
                      key={ln.line_id || ln.name}
                      className="grid grid-cols-[minmax(0,1fr)_90px_90px_90px_100px] px-3 py-1.5 text-xs items-center hover:bg-muted/30"
                      data-testid={`pkg-sync-line-${ln.line_id || ln.name}`}
                    >
                      <div className="truncate">
                        <div className="font-medium truncate">{ln.name}</div>
                        <div className="text-[10px] text-muted-foreground">{ln.category}</div>
                      </div>
                      <div className="text-center font-mono">{ln.expected}</div>
                      <div className="text-center font-mono text-emerald-700">{ln.existing}</div>
                      <div className={`text-center font-mono font-semibold ${ln.missing > 0 ? "text-rose-700" : "text-muted-foreground"}`}>
                        {ln.missing}
                      </div>
                      <div className="text-center text-[10px] text-muted-foreground">{ln.frequency}</div>
                    </div>
                  ))}
                </div>
              </div>

              {totals.missing === 0 && (
                <div className="flex items-center gap-2 text-xs text-emerald-700 border border-emerald-300 bg-emerald-50 px-3 py-2" data-testid="pkg-sync-inline-ok">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  {monthLabel} is fully synced — {totals.expected} item{totals.expected === 1 ? "" : "s"} on the calendar.
                </div>
              )}

              {totals.missing > 0 && (
                <div className="flex items-center gap-2 text-xs text-amber-800 border border-amber-300 bg-amber-50 px-3 py-2" data-testid="pkg-sync-inline-diff">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {totals.missing} item{totals.missing === 1 ? "" : "s"} in {monthLabel} not yet on the calendar. Click <strong>Create missing</strong> to add them.
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-none" data-testid="pkg-sync-close">Close</Button>
          <Button
            onClick={createMissing}
            disabled={!diff || totals.missing === 0 || creating}
            className="rounded-none"
            data-testid="pkg-sync-create-missing"
          >
            {creating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1" />}
            Create {totals.missing || ""} missing
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SummaryTile({ label, value, tone = "default", testid }) {
  const cls = tone === "rose" ? "border-rose-300 bg-rose-50 text-rose-700"
    : tone === "emerald" ? "border-emerald-300 bg-emerald-50 text-emerald-700"
    : tone === "muted" ? "border-border bg-muted/30 text-muted-foreground"
    : "border-border bg-background";
  return (
    <div className={`border px-3 py-2 ${cls}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide opacity-80">{label}</div>
      <div className="text-2xl font-bold font-mono mt-0.5">{value}</div>
    </div>
  );
}

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
